import React, { useState } from "react";

/**
 * Iter 31.239 — One-glance OAuth pairing diagnostic.
 *
 * Fetches the discovery chain definition from /api/mcp/pair, then
 * live-probes the public read-side (steps 1-3) and reports green/red
 * per step. Write-side steps (DCR / authorize / token / SSE) are
 * documented but not auto-executed because they need PKCE + consent.
 *
 * Use any time Claude.ai pairing acts up — a single click localises
 * the break (typically: ingress not routing .well-known/* to backend,
 * or static fallback serving wrong MIME).
 */
export default function PairingDiagnostic() {
    const [chain, setChain] = useState(null);
    const [results, setResults] = useState({});
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);

    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

    const run = async () => {
        setRunning(true);
        setError(null);
        setResults({});
        try {
            const r = await fetch(`${base}/api/mcp/pair`);
            if (!r.ok) {
                setError(`/api/mcp/pair returned HTTP ${r.status}`);
                setRunning(false);
                return;
            }
            const payload = await r.json();
            setChain(payload);

            // Live-probe the public read-side steps only (1, 2, 3).
            const probeable = (payload.discovery_chain || []).filter(
                (s) => s.method === "GET" && s.step <= 3,
            );
            const out = {};
            for (const step of probeable) {
                try {
                    // redirect: "follow" so we see the FINAL response.
                    // We treat a successful 200 application/json as OK
                    // even if step 1 (which expects 301) got followed —
                    // because reaching the JSON is what actually matters.
                    const resp = await fetch(step.url, { redirect: "follow" });
                    const ct = resp.headers.get("content-type") || "";
                    const okJson =
                        resp.ok && ct.toLowerCase().includes("application/json");
                    const redirected = resp.url !== step.url;
                    out[step.step] = {
                        ok: okJson,
                        status: resp.status,
                        content_type: ct || "(none)",
                        final_url: resp.url,
                        redirected,
                    };
                } catch (e) {
                    out[step.step] = {
                        ok: false,
                        status: "ERR",
                        content_type: "(fetch failed)",
                        final_url: step.url,
                        error: String(e),
                    };
                }
            }
            setResults(out);
        } catch (e) {
            setError(String(e));
        } finally {
            setRunning(false);
        }
    };

    const dot = (ok, neutral) => (
        <span
            aria-hidden
            className={`inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle ${
                neutral
                    ? "bg-ink/30"
                    : ok
                    ? "bg-emerald-600"
                    : "bg-stamp-red"
            }`}
        />
    );

    return (
        <div
            className="paper-card border border-ink/30 p-4 mb-4"
            data-testid="admin-pairing-diagnostic"
        >
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <div>
                    <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                        Pairing Diagnostic · OAuth Discovery
                    </h3>
                    <p className="font-body text-xs text-ink-soft mt-1 max-w-2xl">
                        Iter 31.239 — walks the 7-step Claude.ai connector
                        handshake. Auto-probes public read-side (steps 1–3);
                        write-side (DCR/authorize/token/SSE) is documented
                        for manual replay via{" "}
                        <code>/api/mcp/pair</code>.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={run}
                    disabled={running}
                    data-testid="pairing-diagnostic-run-btn"
                    className="font-body text-xs uppercase tracking-[0.18em] px-3 py-1 border border-ink/40 hover:bg-ink/5"
                >
                    {running ? "Walking…" : chain ? "Re-run" : "Run"}
                </button>
            </div>

            {error && (
                <div
                    className="font-body text-xs text-stamp-red mt-2"
                    data-testid="pairing-diagnostic-error"
                >
                    {error}
                </div>
            )}

            {chain && (
                <ol
                    className="mt-3 space-y-2 font-body text-xs"
                    data-testid="pairing-diagnostic-chain"
                >
                    {chain.discovery_chain.map((step) => {
                        const r = results[step.step];
                        const probeable = step.method === "GET" && step.step <= 3;
                        return (
                            <li
                                key={step.step}
                                className="border border-ink/15 p-2"
                                data-testid={`pairing-step-${step.step}`}
                            >
                                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                    <div className="flex items-baseline">
                                        {dot(r?.ok, !probeable)}
                                        <span className="uppercase tracking-[0.16em] text-ink">
                                            Step {step.step} · {step.name}
                                        </span>
                                    </div>
                                    <code className="text-[10px] text-ink-soft">
                                        {step.method} {step.url}
                                    </code>
                                </div>
                                {probeable && r && (
                                    <div
                                        className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[10px] text-ink-soft"
                                        data-testid={`pairing-step-${step.step}-result`}
                                    >
                                        <div>
                                            status:{" "}
                                            <span className="text-ink">{String(r.status)}</span>
                                        </div>
                                        <div>
                                            content-type:{" "}
                                            <span className="text-ink">{r.content_type}</span>
                                        </div>
                                        <div>
                                            redirected:{" "}
                                            <span className="text-ink">
                                                {r.redirected ? "yes" : "no"}
                                            </span>
                                        </div>
                                        {r.redirected && (
                                            <div className="sm:col-span-3 truncate">
                                                final:{" "}
                                                <code className="text-ink">{r.final_url}</code>
                                            </div>
                                        )}
                                        {r.error && (
                                            <div className="sm:col-span-3 text-stamp-red">
                                                {r.error}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {!probeable && (
                                    <div className="mt-1 text-[10px] text-ink-soft italic">
                                        Manual step — see <code>/api/mcp/pair</code>{" "}
                                        for params + expected response.
                                    </div>
                                )}
                                {step.note && (
                                    <div className="mt-1 text-[10px] text-ink-soft">
                                        {step.note}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}
